import {
  DELIMITER_OPTIONS_MAP,
  Delimiter,
  LocaleCode,
  LocaleCodeToDateFormatMap,
  LocaleCodeToDelimiterMap,
  LocaleCodeToNumberFormatExampleMap,
} from '@doss/common-all'
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import _, { isEmpty, isNil } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { RxCheckCircled, RxExclamationTriangle } from 'react-icons/rx'
import { tss } from 'tss-react/mui'
import { UploadFile } from '~/@/components/inputs/file/upload-file'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import {
  ApplicationOrientation,
  DocumentType,
  useCsvInsertedTableRowSubscription,
  useGetActiveUsersQuery,
  useUploadTableDataCsvMutation,
} from '~/common/generated/apollo-operations'
import { useApplicationOrientation } from '~/common/hooks/useApplicationOrientation'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { gray, green, uiColors } from '~/common/styles/colors'
import {
  BulkUpdateTableRow,
  createAndDownloadHeadersCsv,
  getAllColumnsForCsvUpload,
  parseUploadedCsvWithStream,
} from '~/common/utils/csv'
import { getDropzoneAcceptOptionForDocumentTypes } from '~/common/utils/document'
import { CsvTable } from '~/components/tables/types'
import { LoadingSpinner } from '~/components/ui/atoms/loading-spinner/LoadingSpinner'
import { Text } from '~/components/ui/atoms/text/Text'
import { LoadingButton } from '~/components/ui/molecules/loading-button/LoadingButton'
import { Modal } from '~/components/ui/molecules/modal/Modal'
import { Button } from '../../ui/atoms/button/Button'
import { useDossSnackbar } from '../DossSnackbar'
import { ErrorMessage } from '../ErrorMessage'
import { TableBulkUploadRow } from './TableBulkUploadRow'
type TableBulkUploadDialogProps = {
  table: CsvTable
  open: boolean
  onClose: (success?: boolean) => void
  parentRowId?: string
}

const PREVIEW_ROW_COUNT = 15

/**
 * Dialog to bulk upload data to a table from a CSV file.
 */
export function TableBulkUploadDialog({
  table,
  open,
  onClose,
  parentRowId,
}: TableBulkUploadDialogProps) {
  const { orientation } = useApplicationOrientation()
  const isChildTable = !isNil(table.parentTable)
  const canHaveChildTables = isNil(table.parentTable) && !isChildTable
  const snackbar = useDossSnackbar()
  const theme = useTheme()
  const { user, locale } = useGlobalContext()

  const { t } = useTypedTranslation()
  const [uploadTableDataCsv, { loading }] = useUploadTableDataCsvMutation({
    update(cache) {
      cache.evict({ fieldName: 'paginatedTableRows' })
    },
  })
  const [files, setFiles] = useState<Record<string, File>>({})
  const [isParsing, setIsParsing] = useState(false)
  const defaultDelimiter = LocaleCodeToDelimiterMap[locale]
  const defaultLocaleOptions = _.uniq([locale, LocaleCode.EN_US])
  const [selectedDelimiter, setSelectedDelimiter] = useState<Delimiter>(defaultDelimiter)
  const [selectedDateLocale, setSelectedDateLocale] = useState<LocaleCode>(locale)
  const [selectedNumberLocale, setSelectedNumberLocale] = useState<LocaleCode>(locale)
  const [validatedRows, setValidatedRows] = useState<BulkUpdateTableRow[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [fileError, setFileError] = useState<string>('')
  const [insertionProgressCount, setInsertionProgressCount] = useState(0)
  const { classes } = useStyles()

  const { data: activeUsersData } = useGetActiveUsersQuery({ skip: !open })

  const userIdToDisplayName = useMemo(() => {
    const map = new Map<string, string>()
    for (const u of activeUsersData?.activeUsers ?? []) {
      const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
      map.set(u.id, name || u.email || u.id)
    }
    return map
  }, [activeUsersData?.activeUsers])

  const resolveUserDisplay = useCallback(
    (userId: string) => userIdToDisplayName.get(userId) ?? userId,
    [userIdToDisplayName],
  )

  useEffect(() => {
    if (!isEmpty(files)) {
      const file = Object.values(files)[0]
      // Check that the csv has the expected headers for the table
      // File has valid headers, continue with parsing
      const reader = new FileReader()
      reader.onload = (ev) => {
        setIsParsing(true)
        parseUploadedCsvWithStream(
          table,
          ev,
          setFileError,
          setRowCount,
          setValidatedRows,
          setIsParsing,
          selectedDelimiter,
          selectedDateLocale,
          selectedNumberLocale,
          user?.organizationTimeZone ?? dayjs.tz.guess(),
          t,
          parentRowId,
        )
      }
      reader.readAsText(file)
    } else {
      // File has invalid headers, abort and show an error message
      setFileError('')
      setValidatedRows([])
      setRowCount(0)
    }
  }, [
    files,
    table,
    selectedDelimiter,
    selectedDateLocale,
    selectedNumberLocale,
    user?.organizationTimeZone,
    parentRowId,
    t,
  ])

  // Listen to changes for the csv row insertion
  useCsvInsertedTableRowSubscription({
    variables: { input: { userId: user?.id ?? '', tableId: table.id, parentRowId } },
    skip: !user,
    onData: ({ data }) => {
      if (!data.data) {
        return
      }
      const updatedProgress = data.data.csvInsertedTableRow

      if (updatedProgress.objectId === table.id) {
        setInsertionProgressCount(updatedProgress.completedCount)
      }
    },
  })

  const rowsWithoutErrors = useMemo(() => {
    return validatedRows.filter((row) => !row.hasError)
  }, [validatedRows])

  const rowsWithError = useMemo(() => {
    return validatedRows.filter((row) => row.hasError)
  }, [validatedRows])

  const progress = (insertionProgressCount / rowCount) * 100

  const rowsWithErrorMessage: string | null = useMemo(() => {
    if (rowsWithError.length > 0) {
      return t('table.upload.rows_error')
    } else {
      return null
    }
  }, [rowsWithError, t])

  const previewRows = useMemo(() => {
    // Only slice the rows if there are no errors to show a preview
    if (rowsWithError.length === 0 && validatedRows.length > 0) {
      return validatedRows.slice(0, PREVIEW_ROW_COUNT)
    }
    return []
  }, [validatedRows, rowsWithError.length])

  const showProceedBanner = useMemo(() => {
    return rowsWithError.length === 0 && validatedRows.length > 0
  }, [rowsWithError.length, validatedRows.length])

  const handleSave = async () => {
    if (!isEmpty(files) && !isEmpty(rowsWithoutErrors)) {
      const file = Object.values(files)[0]
      try {
        await uploadTableDataCsv({
          variables: {
            input: {
              tableId: table.id,
              file,
              delimiter: selectedDelimiter,
              dateLocale: selectedDateLocale,
              numberLocale: selectedNumberLocale,
              parentRowId,
            },
          },
        })
        snackbar.showSuccess(t(`table.upload.success`, { count: rowCount, tableName: table.name }))
        handleClose(true)
      } catch (err) {
        snackbar.showError(t(`table.upload.error`, { error: err.message }))
      }
    }
  }

  const handleClose = (success?: boolean) => {
    onClose(success)
    setFiles({})
    setFileError('')
    setValidatedRows([])
    setRowCount(0)
  }

  const columnNames = getAllColumnsForCsvUpload(table, !isNil(table.parentTable))

  let buttonText = t('table.upload.insert_rows')

  if (rowCount === 1) {
    buttonText = t('table.upload.insert_row')
  } else if (rowCount > 1) {
    buttonText = t('table.upload.insert_row_num', { count: rowCount })
  }

  return (
    <div dir={orientation === ApplicationOrientation.RIGHT_TO_LEFT ? 'rtl' : 'ltr'}>
      <Modal
        actions={
          <div className="buttonContainer">
            <LoadingButton loading={loading} onClick={handleSave} size="small" variant="secondary">
              {buttonText}
            </LoadingButton>
          </div>
        }
        fullScreen={!isEmpty(files)}
        fullWidth
        onClose={() => handleClose(false)}
        open={open}
        showCloseButton={!loading}
        title={t('table.upload.modal_title', { name: table.name })}
      >
        {isEmpty(files) && (
          <div className="flex-start flex flex-col items-start gap-3">
            <Button
              onClick={() =>
                createAndDownloadHeadersCsv(
                  table,
                  !canHaveChildTables,
                  LocaleCodeToDelimiterMap[locale],
                )
              }
              variant="tertiary"
            >
              {t('table.upload.download_csv_template')}
            </Button>
            <div className="mb-2 flex w-full max-w-full justify-start gap-2">
              <Dropdown
                label={t('csv.delimiter')}
                onChange={(newValue) => {
                  setSelectedDelimiter(newValue ?? defaultDelimiter)
                }}
                options={(Object.keys(DELIMITER_OPTIONS_MAP) as Delimiter[]).map((option) => ({
                  value: option,
                  label: DELIMITER_OPTIONS_MAP[option],
                }))}
                value={selectedDelimiter}
                wrapperClassName="grow min-w-max"
              />
              <Dropdown
                label={t('csv.date_format')}
                onChange={(newValue) => {
                  setSelectedDateLocale(newValue ?? locale)
                }}
                options={defaultLocaleOptions.map((option) => ({
                  value: option,
                  label: LocaleCodeToDateFormatMap[option],
                }))}
                value={selectedDateLocale}
                wrapperClassName="grow min-w-max"
              />
              <Dropdown
                label={t('csv.number_format')}
                onChange={(newValue) => {
                  setSelectedNumberLocale(newValue ?? locale)
                }}
                options={defaultLocaleOptions.map((option) => ({
                  value: option,
                  label: LocaleCodeToNumberFormatExampleMap[option],
                }))}
                value={selectedNumberLocale}
                wrapperClassName="grow min-w-max"
              />
            </div>
          </div>
        )}
        <UploadFile
          allowMultiple={false}
          dropzoneOptions={{
            accept: getDropzoneAcceptOptionForDocumentTypes([DocumentType.CSV]),
          }}
          files={files}
          setFiles={setFiles}
        />
        {fileError && <ErrorMessage message={fileError} />}
        {rowsWithErrorMessage && <ErrorMessage message={rowsWithErrorMessage} />}
        {showProceedBanner && !loading && (
          <div className={classes.helperText}>
            <Text>
              {t('table.upload.preview_row_count', {
                previewRowCount:
                  validatedRows.length < PREVIEW_ROW_COUNT
                    ? validatedRows.length
                    : PREVIEW_ROW_COUNT,
                totalRowCount: validatedRows.length,
              })}
            </Text>
          </div>
        )}
        {isParsing && <LoadingSpinner type="auth" />}
        {loading && (
          <>
            <div className={classes.loadingContainer}>
              <div className={classes.loadingMessage}>
                <RxExclamationTriangle color={theme.palette.common.doss.amber[10]} size={24} />
                <Text typography="body3">{t('table.upload.stay_on_page')}</Text>
              </div>
            </div>
            <div className={classes.progressContainer}>
              <LinearProgress
                className={classes.progressBar}
                value={progress}
                variant="determinate"
              />
              <div className={classes.progressCount}>
                <Text typography="body1">
                  {t('table.upload.progress', {
                    insertionProgressCount,
                    validatedRowsLength: validatedRows.length,
                  })}
                </Text>
              </div>
            </div>
          </>
        )}
        {!isEmpty(files) && !loading && (
          <>
            <TableContainer className={classes.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('table.upload.row')}</TableCell>
                    {columnNames.map((columnName) => (
                      <TableCell key={`column-${columnName}`}>{columnName}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsWithError.length === 0 ? previewRows : rowsWithError).map((row, idx) => (
                    <TableBulkUploadRow
                      key={idx}
                      resolveUserDisplay={resolveUserDisplay}
                      row={row}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {showProceedBanner && (
              <div className={classes.successMessage}>
                <RxCheckCircled />
                <Text>{t('table.upload.validated_rows')}</Text>
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  )
}

const useStyles = tss.create(({ theme }) => ({
  tableContainer: {
    paddingBlock: '0',
    paddingInline: '8px',
    border: `1px solid ${uiColors.border.normal}`,
    borderRadius: '2px',
    margin: '8px',
    width: '100%',
  },
  tableRow: { '&:last-child td, &:last-child th': { border: 0 }, height: '32px' },
  emptyState: {
    textAlign: 'center',
    border: 0,
    height: '50px',
  },
  helperText: {
    padding: '8px',
  },
  cell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
  },
  successMessage: {
    margin: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
    flexDirection: 'row',
    backgroundColor: green[5],
    border: `1px solid ${green[10]}`,
    color: green[10],
    padding: '8px',
    borderRadius: '2px',
    width: 'fit-content',
  },
  loadingMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexDirection: 'row',
    margin: '8px',
    padding: '8px',
    color: gray[10],
    borderRadius: '2px',
    height: '50vh',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'flex-end',
  },
  loadingContainer: {
    height: '50vh',
  },
  progressBar: {
    width: '100%',
    backgroundColor: `${theme.palette.common.doss.blue[2]}`,
    '& .MuiLinearProgress-bar': {
      backgroundColor: `${theme.palette.common.doss.brightBlue}`,
    },
  },
  progressContainer: {
    maxWidth: '600px',
    marginBlock: '0',
    marginInline: 'auto',
    display: 'block',
    position: 'relative',
    boxSizing: 'border-box',
  },
  progressCount: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    marginTop: '8px',
  },
}))
